// First-run onboarding beats (ported from the reference build, rewritten to
// fit this UI): one non-modal hint chip parked in the bottom third, shown
// during the first ~60 s of a run. Beats run in order — MOVE → DASH →
// COLLECT → LEVEL UP — and each dismisses when the player actually does the
// thing (its gate) or its time window closes. Shown once per profile:
// game.ts persists access.onboarding_seen when the sequence completes.

import { ensureUiStyles } from './theme.js';
import { detectTouchDevice } from './storage.js';
import type { GameEvent } from '../sim/events.js';

type Gate = 'move' | 'dash' | 'collect' | 'levelup';

// The beat table: per-device copy plus the window (run seconds) a beat stays
// up before giving up and moving on. Gates are measured from the sim.
const BEATS: readonly { gate: Gate; window: number; touch: string; keyboard: string }[] = [
  { gate: 'move', window: 10, touch: 'Hold the bottom zone and drag to steer', keyboard: 'WASD / arrows to move' },
  { gate: 'dash', window: 12, keyboard: 'SPACE to dash',
    touch: 'While steering, double-tap with a second finger to dash' },
  { gate: 'collect', window: 18, touch: 'Fly over the green motes', keyboard: 'Fly over the green motes' },
  { gate: 'levelup', window: 20, touch: 'Pick a boon', keyboard: 'Pick a boon' }
];

// Move gate: above half cruise speed (player.move_speed is 8) for a moment.
const MOVE_SPEED = 4;
const MOVE_HOLD_S = 0.5;

export class Onboarding {
  private readonly el: HTMLElement;
  private running = false;
  private beat = 0;
  private beatStart = 0;
  private lastT = 0;
  private moveHeld = 0;
  private dashed = false;
  private collected = false;
  private picked = false;
  // Seeded from the hardware; the first real input sample refines it.
  private touch: boolean = detectTouchDevice();
  private readonly gates: Record<Gate, () => boolean> = {
    move: () => this.moveHeld >= MOVE_HOLD_S,
    dash: () => this.dashed,
    collect: () => this.collected,
    levelup: () => this.picked
  };

  constructor(parent: HTMLElement, private readonly onDone: () => void) {
    ensureUiStyles();
    this.el = document.createElement('div');
    this.el.className = 'la-onboard';
    this.el.hidden = true;
    this.el.setAttribute('role', 'status');
    parent.appendChild(this.el);
  }

  /** Dominant device from the input sample: touch copy on real touch input,
   * keyboard copy on mouse/gamepad. 'keyboard' is the sample's idle default
   * on every device, so it must not clobber the touch seed on phones. */
  setDevice(device: string): void {
    if (device === 'touch') this.touch = true;
    else if (device === 'mouse' || device === 'gamepad') this.touch = false;
    if (!this.el.hidden && this.beat < BEATS.length) {
      this.el.textContent = this.touch ? BEATS[this.beat].touch : BEATS[this.beat].keyboard;
    }
  }

  /** Called on every (re)start; a profile that has seen it stays inert. */
  reset(seen: boolean): void {
    this.running = !seen;
    this.beat = 0;
    this.beatStart = 0;
    this.lastT = 0;
    this.moveHeld = 0;
    this.dashed = false;
    this.collected = false;
    this.picked = false;
    this.el.hidden = true;
  }

  /** Gate events off the world.events bus (same feed as the other handlers). */
  onEvent(e: GameEvent): void {
    if (!this.running) return;
    if (e.type === 'dash' && e.payload.phase === 'start') this.dashed = true;
    else if (e.type === 'pickup' && e.payload.kind === 'xp_mote') this.collected = true;
    else if (e.type === 'levelup_pick') this.picked = true;
  }

  /** Per-frame hook from the render path; `active` is false while the
   * loadout screen, pause, or game over owns the UI (the chip just hides). */
  update(t: number, speed: number, active: boolean): void {
    if (!this.running) return;
    const dt = Math.max(0, t - this.lastT);
    this.lastT = t;
    if (!active) {
      this.el.hidden = true;
      return;
    }
    if (speed > MOVE_SPEED) this.moveHeld += dt;
    else this.moveHeld = 0;
    // A beat ends on its gate or its window; skip beats already satisfied.
    while (this.beat < BEATS.length && (this.gates[BEATS[this.beat].gate]() || t - this.beatStart >= BEATS[this.beat].window)) {
      this.beat++;
      this.beatStart = t;
    }
    if (this.beat >= BEATS.length) {
      this.running = false;
      this.el.hidden = true;
      this.onDone();
      return;
    }
    this.el.textContent = this.touch ? BEATS[this.beat].touch : BEATS[this.beat].keyboard;
    this.el.hidden = false;
  }
}
